import { type Message } from '@ag-ui/core';

import { isVideoUrl } from '../../tokens/mediaFormatUtils';
import { isImageEdit } from '../../tools/fileEditComplete';
import {
  findToolNameByCallId,
  isFileEditCompleteResult,
  isGenerateTextResult,
  parseToolContent,
} from '../../tools/validation';
import { type CanvasTokenNode, type CanvasTokenTextNode } from '../slices/createCanvasTokenNodeSlice';

const DEFAULT_FONT_SIZE = 16;
const DEFAULT_LINE_HEIGHT = 1.2;

/**
 * Extracts CanvasTokenNode entries from chat thread messages by parsing
 * tool results for file_edit_complete and generate_text tools.
 *
 * Uses deterministic IDs derived from toolCallId so re-processing the same
 * messages produces stable keys.
 */
export function extractChatHistoryNodes(messages: Message[]): Record<string, CanvasTokenNode> {
  const result: Record<string, CanvasTokenNode> = {};

  for (const message of messages) {
    if (message.role !== 'tool') {
      continue;
    }

    const { content, toolCallId } = message;
    const toolName = findToolNameByCallId(toolCallId, messages);
    const parsed = parseToolContent(toolName, content);

    if (!parsed) {
      continue;
    }

    const params = { toolCallName: toolName ?? '', toolCallResult: parsed };

    if (isFileEditCompleteResult(params)) {
      const { edits, prompt: toolPrompt, timestamp } = params.toolCallResult;
      const updatedAtSpread = timestamp ? { updatedAt: timestamp } : {};
      for (const [index, edit] of edits.entries()) {
        const id = `${toolPrompt.runId}-${index}`;

        if (isImageEdit(edit)) {
          result[id] = {
            fileName: edit.fileName ?? '',
            group: edit.group ? { ...edit.group, id: edit.group.id } : { id },
            media: { image: edit.url, original: edit.url },
            type: isVideoUrl(edit.url) ? 'video' : 'image',
            ...updatedAtSpread,
          };
        } else {
          const textNode: CanvasTokenTextNode = {
            fileName: '',
            group: edit.group ? { ...edit.group, id: edit.group.id } : { id },
            textNode: {
              fontColor: edit.textNode.fontColor,
              fontFamily: edit.textNode.fontFamily,
              fontSize: edit.textNode.fontSize,
              fontSource: edit.textNode.fontSource ?? 'builtIn',
              ...(edit.textNode.fontFileUrl ? { fontFileUrl: edit.textNode.fontFileUrl } : {}),
              fontVariant: edit.textNode.fontVariant ?? '',
              letterSpacing: edit.textNode.letterSpacing ?? 0,
              lineHeight: edit.textNode.lineHeight ?? DEFAULT_LINE_HEIGHT,
              text: edit.textNode.text,
              textAlign: edit.textNode.textAlign,
            },
            type: 'text',
            ...updatedAtSpread,
          };
          result[id] = textNode;
        }
      }
    } else if (isGenerateTextResult(params)) {
      const id = toolCallId;
      const { fontFileUrl, fontMetadataUrl, fontSource, height, positionX, positionY, textNode, timestamp, width } =
        params.toolCallResult;
      const updatedAtSpread = timestamp ? { updatedAt: timestamp } : {};
      result[id] = {
        fileName: '',
        group: {
          height,
          id,
          width,
          x: positionX,
          y: positionY,
        },
        textNode: {
          fontColor: textNode.fontColor,
          fontFamily: textNode.fontFamily,
          fontSize: textNode.fontSize ?? DEFAULT_FONT_SIZE,
          fontSource: fontSource ?? 'builtIn',
          ...(fontFileUrl ? { fontFileUrl } : {}),
          ...(fontMetadataUrl ? { fontMetadataUrl } : {}),
          fontVariant: textNode.fontVariant ?? '',
          letterSpacing: textNode.letterSpacing ?? 0,
          lineHeight: textNode.lineHeight ?? DEFAULT_LINE_HEIGHT,
          text: textNode.text,
          textAlign: textNode.textAlign,
        },
        type: 'text',
        ...updatedAtSpread,
      };
    }
  }

  return result;
}
